import type { Profile } from "@frontier/protocol/contracts";

export class ProfileSync {
  private epoch = 0;
  private writeGeneration = 0;
  private readSequence = 0;
  private session: { playerId: string; token: string } | null = null;
  private reader: AbortController | null = null;
  private name: Pick<Profile, "name" | "nameRevision"> | null = null;
  private latest: Profile | null = null;

  constructor(
    private readonly readProfile: (
      token: string,
      signal: AbortSignal,
    ) => Promise<Profile>,
    private readonly publish: (profile: Profile) => void,
    private readonly reportFailure: (message: string) => void,
  ) {}

  start(profile: Profile, token: string): void {
    this.reset();
    this.session = { playerId: profile.playerId, token };
    this.publishCurrent(profile);
  }

  ticket(): number {
    return this.epoch;
  }

  current(): Profile | null {
    return this.latest;
  }

  commit(profile: Profile, ticket: number): boolean {
    if (ticket !== this.epoch || profile.playerId !== this.session?.playerId)
      return false;
    if (!this.acceptsHull(profile)) {
      void this.refresh(ticket);
      return false;
    }
    this.writeGeneration++;
    this.publishCurrent(profile);
    void this.refresh(ticket);
    return true;
  }

  invalidate(profile: Profile): Promise<Profile | null> {
    return profile.playerId === this.session?.playerId
      ? this.refresh(this.epoch)
      : Promise.resolve(null);
  }

  async refresh(ticket = this.epoch): Promise<Profile | null> {
    const session = this.session;
    if (!session || ticket !== this.epoch) return null;
    this.reader?.abort();
    const controller = new AbortController();
    this.reader = controller;
    const generation = this.writeGeneration;
    const sequence = ++this.readSequence;
    try {
      const profile = await this.readProfile(session.token, controller.signal);
      if (
        controller.signal.aborted ||
        ticket !== this.epoch ||
        generation !== this.writeGeneration ||
        sequence !== this.readSequence ||
        session !== this.session
      )
        return null;
      if (profile.playerId !== session.playerId)
        throw new Error("Ответ профиля относится к другому пилоту");
      if (!this.acceptsHull(profile)) return null;
      this.publishCurrent(profile);
      return this.latest;
    } catch (error) {
      if (
        !controller.signal.aborted &&
        ticket === this.epoch &&
        sequence === this.readSequence
      ) {
        const message =
          error instanceof Error
            ? error.message
            : "Не удалось обновить профиль";
        console.warn("profile-refresh-failed", {
          playerId: session.playerId,
          message,
        });
        this.reportFailure(
          "Не удалось обновить профиль. Проверь связь и повтори подключение.",
        );
      }
      return null;
    } finally {
      if (this.reader === controller) this.reader = null;
    }
  }

  cancelReads(): void {
    this.reader?.abort();
    this.reader = null;
    this.writeGeneration++;
    this.readSequence++;
  }

  reset(): void {
    this.cancelReads();
    this.epoch++;
    this.session = null;
    this.name = null;
    this.latest = null;
  }

  private acceptsHull(profile: Profile): boolean {
    const current = this.latest;
    if (current?.hullRevision === undefined) return true;
    return profile.hullRevision !== undefined &&
      (profile.hullRevision > current.hullRevision ||
        (profile.hullRevision === current.hullRevision && profile.hullId === current.hullId));
  }

  private publishCurrent(profile: Profile): void {
    this.latest = this.withCurrentName(profile);
    this.publish(this.latest);
  }

  private withCurrentName(profile: Profile): Profile {
    const current = this.name;
    if (
      current &&
      current.nameRevision !== undefined &&
      (profile.nameRevision ?? -1) <= current.nameRevision
    ) {
      return profile.name === current.name &&
        profile.nameRevision === current.nameRevision
        ? profile
        : { ...profile, ...current };
    }
    this.name = {
      name: profile.name,
      ...(profile.nameRevision === undefined
        ? {}
        : { nameRevision: profile.nameRevision }),
    };
    return profile;
  }
}
